/**
 * Helpers per serializzare/deserializzare il TaskScope nella query string.
 *
 * Formato URL:
 *  - `/tasks?project=12`     → scope progetto
 *  - `/tasks?scope=inbox`    → scope inbox
 *  - nessun parametro       → scope non specificato (il caller decide il default)
 *
 * Lo scope è la fonte di verità della pagina Task (FE plan sezione 4).
 */

import type { TaskScope } from "../../types/tasks";

/**
 * Converte lo scope in SearchParams (mutabile, per integrarsi con useSearchParams).
 */
export const scopeToSearchParams = (
  scope: TaskScope | null,
  params: URLSearchParams
): URLSearchParams => {
  const next = new URLSearchParams(params);
  next.delete("project");
  next.delete("scope");
  if (scope?.kind === "project") {
    next.set("project", String(scope.projectId));
  } else if (scope?.kind === "inbox") {
    next.set("scope", "inbox");
  }
  return next;
};

/**
 * Legge lo scope dalla query string. Ritorna null se non è specificato
 * (il caller applica il fallback a primo progetto / inbox).
 */
export const parseScopeFromQuery = (
  params: URLSearchParams
): TaskScope | null => {
  const projectIdStr = params.get("project");
  if (projectIdStr !== null) {
    const projectId = Number(projectIdStr);
    if (Number.isFinite(projectId) && projectId > 0) {
      return { kind: "project", projectId };
    }
    return null;
  }
  if (params.get("scope") === "inbox") {
    return { kind: "inbox" };
  }
  return null;
};

/**
 * Confronta due scope per uguaglianza strutturale (no riferimento).
 */
export const scopesEqual = (
  a: TaskScope | null,
  b: TaskScope | null
): boolean => {
  if (a === null && b === null) return true;
  if (a === null || b === null) return false;
  if (a.kind !== b.kind) return false;
  if (a.kind === "project" && b.kind === "project") {
    return a.projectId === b.projectId;
  }
  return true;
};

/**
 * Chiave localStorage per persistere l'ultimo scope valido.
 * User-specific per evitare contaminazioni tra account sulla stessa macchina.
 */
export const buildScopeStorageKey = (userId?: number | string | null): string => {
  const uid = userId != null && userId !== "" ? String(userId) : "anon";
  return `hexa:tasks:last-scope:${uid}`;
};

/**
 * Salva lo scope corrente in localStorage. Null rimuove la chiave.
 */
export const persistScope = (
  scope: TaskScope | null,
  userId?: number | string | null
): void => {
  try {
    const key = buildScopeStorageKey(userId);
    if (scope === null) {
      localStorage.removeItem(key);
      return;
    }
    localStorage.setItem(key, JSON.stringify(scope));
  } catch {
    // localStorage può lanciare in incognito o quota: silently ignore.
  }
};

/**
 * Legge lo scope persistito, validando che sia ancora una shape riconoscibile.
 */
export const readPersistedScope = (
  userId?: number | string | null
): TaskScope | null => {
  try {
    const raw = localStorage.getItem(buildScopeStorageKey(userId));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed && parsed.kind === "inbox") return { kind: "inbox" };
    if (
      parsed &&
      parsed.kind === "project" &&
      typeof parsed.projectId === "number" &&
      parsed.projectId > 0
    ) {
      return { kind: "project", projectId: parsed.projectId };
    }
    return null;
  } catch {
    return null;
  }
};
